import HighlightGenerationListener from "components/v2/HighlightGenerationListener";
import antdNotification, { showNotification } from "components/v2/notification/notification";
import { ToastNotificationContainer } from "components/v2/toastNotificationContainer";
import { aspect_ratio } from "config";
import { ASPECT_RATIO, CroppingTypes, FancodeUsers, ViaComUsers } from "constants/phase";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import { createStructuredSelector } from "reselect";
import { selectAllAutoFlipCategories } from "store/category/selectors";
import { generateAutoFlipClip, generateInputRatioClip } from "store/clip/actions";
import { getClipListDetails } from "store/clipList/actions";
import { selectSelectedUserId, selectUserEntities } from "store/user/selectors";
import {
  getHighlightsFromLocalStorage,
  getHighlightStepperState,
  setHighlightStepperState,
  setHighlightsToLocalStorage,
} from "utils/Storage";

import { HighlightCreationContext } from "./context";

const HighlightCreationProvider = ({
  children,
  generateAutoFlipClip,
  generateInputRatioClip,
  getClipListDetails,
  autoFlipCategories,
  userId,
  userEntities,
}) => {
  const [highlights, setHighlights] = useState([]);
  const [showToastNotification, setShowToastNotification] = useState(false);
  const currentStepRef = useRef(0);
  const autoCloseTimerRef = useRef(null);

  const TOTAL_STEPS = 7;

  const handleShowToastNotification = useCallback((message, clipListId) => {
    const notificationKey = `highlight-stepper-${clipListId}`;
    const step = Math.min(currentStepRef.current, TOTAL_STEPS);
    showNotification({
      type: "open",
      key: notificationKey,
      message: <ToastNotificationContainer currentStep={step} totalSteps={TOTAL_STEPS} message={message} />,
      duration: 0,
      placement: "topRight",
    });
    setHighlightStepperState({ clipListId, currentStep: step, message });
  }, []);

  const closeStepperNotification = useCallback(
    (clipListId) => {
      const notificationKey = `highlight-stepper-${clipListId}`;
      if (autoCloseTimerRef.current) {
        clearTimeout(autoCloseTimerRef.current);
        autoCloseTimerRef.current = null;
      }
      currentStepRef.current = 0;
      setShowToastNotification(false);
      setHighlightStepperState(null);
      antdNotification.close(notificationKey);
    },
    [setShowToastNotification],
  );

  const initializeStepperToast = useCallback(
    (message, clipListId) => {
      if (currentStepRef.current === 0) {
        currentStepRef.current = 1;
      } else {
        currentStepRef.current = Math.min(currentStepRef.current + 1, TOTAL_STEPS);
      }
      handleShowToastNotification(message, clipListId);
    },
    [handleShowToastNotification],
  );

  const restoreStepperToast = useCallback(
    (clipListId, progressPercent, clipListIsPreview) => {
      if ((progressPercent <= 0 || progressPercent >= 100) && !clipListIsPreview) return;
      const stepFromProgress = Math.min(Math.max(1, Math.ceil((progressPercent / 100) * TOTAL_STEPS)), TOTAL_STEPS);
      const stored = getHighlightStepperState();
      const storedStep = stored?.clipListId === clipListId && stored.currentStep ? stored.currentStep : 0;
      const step = Math.max(storedStep, stepFromProgress);
      currentStepRef.current = step;
      const message =
        stored?.clipListId === clipListId && step === stored.currentStep && stored.message
          ? stored.message
          : "Highlight generation in progress...";
      setShowToastNotification(true);
      handleShowToastNotification(message, clipListId);
    },
    [handleShowToastNotification, setShowToastNotification],
  );

  const handleHighlightGeneration = useCallback(
    async ({ clipListId, unSyncClips = [], aspectRatio, streamId, streamCategory, clipListClips = [] }) => {
      try {
        setHighlights((prev) => [
          ...prev,
          {
            clipListId,
            streamId,
            aspectRatio,
            status: "PROCESSING",
            isHidden: false,
            clipListClips,
          },
        ]);

        if (unSyncClips.length) {
          const coordinate = aspect_ratio.find((o) => o?.value === aspectRatio)?.coordinate;

          const promises = unSyncClips.map((clipDetails) => {
            if (
              autoFlipCategories &&
              autoFlipCategories.includes(streamCategory) &&
              ((ViaComUsers.includes(userId) && aspectRatio === ASPECT_RATIO["9_18"]) ||
              FancodeUsers.includes(userId) ||
              FancodeUsers.includes(userEntities?.isBillingAdmin)
                ? streamCategory !== "cricket"
                : true)
            ) {
              return generateAutoFlipClip({
                payload: {
                  videoUrl: clipDetails?.videoUrl,
                  clipId: clipDetails?._id,
                  streamId: clipDetails?.streamId,
                  aspectRatio: aspectRatio,
                  clipTitle: clipDetails?.title || clipDetails?.episodeTitle || clipDetails?.id || "",
                },
              });
            }

            return generateInputRatioClip({
              payload: {
                aspectRatio: aspectRatio,
                size: coordinate,
                streamId: clipDetails?.streamId,
                clipId: clipDetails?._id,
                videoUrl: clipDetails?.videoUrl,
                clipTitle: clipDetails?.title || clipDetails?.episodeTitle || clipDetails?.id || "",
                event: CroppingTypes.STATIC,
              },
            });
          });

          promises.push(getClipListDetails({ clipListId, isDragDropHLGeneration: true }));
          await Promise.all(promises);
        } else {
          await getClipListDetails({ clipListId, isDragDropHLGeneration: true });
        }
      } catch (error) {
        console.error(error);
      }
    },
    [
      autoFlipCategories,
      generateAutoFlipClip,
      generateInputRatioClip,
      getClipListDetails,
      userEntities?.isBillingAdmin,
      userId,
    ],
  );

  const handleClipUpdate = useCallback(
    async (clipListId) => {
      try {
        await getClipListDetails({ clipListId, isDragDropHLGeneration: true });
      } catch (error) {
        console.error(error);
      }
    },
    [getClipListDetails],
  );

  const handleRemoveNotification = useCallback((id) => {
    try {
      setHighlights((prev) => {
        const updatedHighlights = prev?.filter((highlight) => {
          if (highlight.clipListId === id) {
            if (highlight.status === "SUCCESS" || highlight.status === "FAILED") {
              return false;
            }
            highlight.isHidden = true;
          }
          return true;
        });

        return updatedHighlights;
      });
    } catch (error) {
      console.error(error);
    }
  }, []);

  useEffect(() => {
    setHighlights(getHighlightsFromLocalStorage());
  }, []);

  useEffect(() => {
    setHighlightsToLocalStorage(highlights);
  }, [highlights]);

  return (
    <HighlightCreationContext.Provider
      value={{
        highlights,
        setHighlights,
        handleHighlightGeneration,
        handleRemoveNotification,
        showToastNotification,
        setShowToastNotification,
        handleShowToastNotification,
        closeStepperNotification,
        initializeStepperToast,
        restoreStepperToast,
        currentStepRef,
        autoCloseTimerRef,
        stepperTotalSteps: TOTAL_STEPS,
      }}
    >
      {children}
      {highlights.map((highlight) => (
        <HighlightGenerationListener
          key={highlight.clipListId}
          streamId={highlight.streamId}
          clipListId={highlight.clipListId}
          aspectRatio={highlight.aspectRatio}
          onClipUpdate={handleClipUpdate}
          clipListClips={highlight.clipListClips}
        />
      ))}
    </HighlightCreationContext.Provider>
  );
};

const mapStateToProps = createStructuredSelector({
  userId: selectSelectedUserId,
  userEntities: selectUserEntities,
  autoFlipCategories: selectAllAutoFlipCategories,
});

const mapDispatchToProps = {
  generateAutoFlipClip,
  generateInputRatioClip,
  getClipListDetails,
};

export default connect(mapStateToProps, mapDispatchToProps)(HighlightCreationProvider);
